import { useMemo } from 'react'

import { BankDetails } from '@/core/api/entities/account/billingInfo'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

export const usePaymentMethodsData = () => {
	const account = useCurrentAccount()

	const accountId = useMemo(() => account?.id || 0, [account?.id])

	const bankDetails = useMemo<BankDetails | undefined>(
		() => account?.billingInfo?.bankDetails,
		[account?.billingInfo?.bankDetails]
	)

	const companyTaxId = useMemo(
		() => account?.billingInfo?.companyDetails?.tax,
		[account?.billingInfo?.companyDetails?.tax]
	)

	// Get billing system country from multiple sources
	const billingSystemCountry = useMemo(
		() =>
			account?.billingInfo?.billingSystem?.country ||
			account?.billingInfo?.billingData?.country ||
			'',
		[
			account?.billingInfo?.billingData?.country,
			account?.billingInfo?.billingSystem?.country,
		]
	)

	// Use billingSystem.name as primary source
	const billingSystemName = useMemo(
		() => account?.billingInfo?.billingSystem?.name || '',
		[account?.billingInfo?.billingSystem?.name]
	)

	const alegraIso = useMemo(
		() => account?.billingInfo?.alegraIso,
		[account?.billingInfo?.alegraIso]
	)

	// Get payment gateway and method from billing info
	const paymentGateway = useMemo(
		() => account?.billingInfo?.paymentGateway || '',
		[account?.billingInfo?.paymentGateway]
	)

	const paymentMethod = useMemo(
		() => account?.billingInfo?.paymentMethod || '',
		[account?.billingInfo?.paymentMethod]
	)

	// Check if automatic payment is available
	// Uses multiple fallbacks since billingSystem may not be loaded
	const showAutomaticPayment = useMemo(() => {
		// 1. Check billingSystemName
		if (billingSystemName) {
			const matchesByName = ['USA', 'MEX', 'ESP'].some((code) =>
				billingSystemName.includes(code)
			)
			if (matchesByName) return true
		}

		// 2. Fallback to alegraIso check
		if (alegraIso) {
			const matchesByIso = ['Alegra USA', 'Alegra MEX', 'Alegra ESP'].includes(
				alegraIso
			)
			if (matchesByIso) return true
		}

		return false
	}, [paymentGateway, billingSystemCountry, billingSystemName, alegraIso])

	const isSEPACountry = useMemo(() => {
		const countryUpper = billingSystemCountry.toUpperCase()

		return [
			// Countries having EUR as default currency
			'FI',
			'AD',
			'AT',
			'PT',
			'BE',
			'ES',
			'CY',
			'EE',
			'FR',
			'DE',
			'GI',
			'GR',
			'IE',
			'IT',
			'LV',
			'LT',
			'LU',
			'MT',
			'MC',
			'NL',
			'SM',
			'SK',
			'SI',
			// Countries not having EUR as default currency
			'BG',
			'HR',
			'CZ',
			'DK',
			'HU',
			'IS',
			'LI',
			'NO',
			'PL',
			'RO',
			'SE',
			'CH',
			'GB',
		].includes(countryUpper)
	}, [billingSystemCountry])

	// Check for SEPA (Spain) - available when country is Spain or payment method is SEPA
	const hasSEPA = useMemo(() => {
		// If we have a definitive country, rely on it
		if (isSEPACountry && !!billingSystemName) {
			return true
		}

		return billingSystemName.includes('ESP') || alegraIso === 'Alegra ESP'
	}, [billingSystemCountry, billingSystemName, alegraIso, paymentMethod])

	return {
		accountId,
		bankDetails,
		companyTaxId,
		billingSystemCountry,
		billingSystemName,
		alegraIso,
		showAutomaticPayment,
		hasSEPA,
	}
}
